import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
    VendorStatTicketsSvg,
    VendorStatClockSvg,
    VendorStatDocumentsSvg,
    VendorTicketsCtaSvg,
    VendorChevronRightSvg,
} from "../../../components/CommonSvg";
import {
    TicketsBarChart,
    TicketStatusDonut,
    CATEGORY_COLORS,
} from "./DashboardCharts";
import { getVendorDashboardRequest } from "../../../redux/vendor/dashboard/action";
import {
    initials,
    shortStamp,
    statusSuffix,
    toView,
} from "../SupportTickets/ticketUtils";

const TICKETS_PATH = "/vendor/support-tickets";

/* a dashboard row points at one ticket, not at the inbox - the ticket no. is
   what the list searches on and the id is what it selects with, so both travel
   in the link and the tickets page opens that ticket straight away */
const ticketDetailPath = (ticket) => {
    const params = new URLSearchParams();
    const no = String(ticket?.no || "").replace(/^#/, "");
    if (no) params.set("ticketNo", no);
    if (ticket?.id) params.set("ticketId", String(ticket.id));
    const query = params.toString();
    return query ? `${TICKETS_PATH}?${query}` : TICKETS_PATH;
};

const DOCUMENTS_PATH = "/vendor/documents";
const MEMBERS_PATH = "/vendor/members";

/* The feed carries no links of its own - each entry only says what happened and
   to what, so the row works out where that lives. Anything we cannot place (a
   password change, a staff edit) stays a plain row rather than a dead link. */
const activityPath = (item) => {
    const category = item?.category;

    if (category === "upload" || category === "download") {
        /* the member export shares the download category with document
           downloads - its title is the only thing that tells them apart */
        if (String(item?.title || "").toLowerCase().includes("member")) {
            return MEMBERS_PATH;
        }
        /* the api sends the folder the file sits in, so the link lands inside
           that folder rather than on the folder grid */
        return item?.referenceCategory
            ? `${DOCUMENTS_PATH}?folder=${encodeURIComponent(item.referenceCategory)}`
            : DOCUMENTS_PATH;
    }

    if (["ticket-new", "ticket-status", "inbound-email"].includes(category)) {
        /* the ticket no. leads the subtitle ("SR-62496 — Store"), and for the
           two ticket feeds the id tails the entry id as well */
        const no = String(item?.sub || "").split("—")[0].trim();
        const idMatch = String(item?.id || "").match(/^ticket-(?:new|status)-(\d+)/);
        return ticketDetailPath({ no: no, id: idMatch ? idMatch[1] : null });
    }

    return null;
};

/* red is kept for what is overdue - an open ticket is just work in hand */
const CARD_ICONS = {
    open: { Icon: VendorStatTicketsSvg, icClass: "vp-ic-blue" },
    category: { Icon: VendorStatDocumentsSvg, icClass: "vp-ic-navy" },
    oldest: { Icon: VendorStatClockSvg, icClass: "vp-ic-amber" },
};

/* A status change is news, not an outcome - a tick would read as "resolved"
   whatever the new status is, so it gets a neutral mark. */
const ACTIVITY_BADGES = {
    "ticket-new": { badge: "＋", color: "#0f6cbd" },
    "ticket-status": { badge: "↻", color: "#69797e" },
    "inbound-email": { badge: "✉", color: "#038387" },
    "share-email": { badge: "↗", color: "#8764b8" },
    upload: { badge: "↑", color: "#0f6cbd" },
    download: { badge: "↓", color: "#038387" },
    "staff-activity": { badge: "•", color: "#ca5010" },
};
const DEFAULT_BADGE = { badge: "•", color: "#69797e" };

/* user events are about a person, so they wear that person's initials */
const badgeText = (item, badge) => {
    if (item.actorName) {
        return initials(item.actorName);
    }
    const match = String(item.sub || "").match(
        /^([A-Za-z]+(?:\s+[A-Za-z]+)+)\s+(?:was|has|updated|added)/
    );
    if (match) {
        return initials(match[1]);
    }
    return badge.badge;
};

/* the store leads a ticket row - "Hillsville Mini Mart · HRA9750" - and the
   person who raised it moves to the second line. The HRA id normally comes
   from the member record; Zoho's store names carry it too, as a fallback. */
const storeHeadline = (ticket) => {
    if (!ticket.storeOnly) return ticket.person;
    const hraId =
        ticket.hraMemberId || (String(ticket.company).match(/HRA\d+/i) || [])[0] || "";
    return [ticket.storeOnly, hraId].filter(Boolean).join(" · ");
};

const secondaryWho = (ticket) => {
    if (ticket.person && ticket.email && ticket.person !== ticket.email) {
        return `${ticket.person} (${ticket.email})`;
    }
    return ticket.person || ticket.email || "";
};

const ticketLine = (ticket) =>
    [ticket.no, [secondaryWho(ticket), ticket.category].filter(Boolean).join(" · ")]
        .filter(Boolean)
        .join(" — ");

const ageUnit = (days) => (days === 1 ? "day" : "days");

const withThousands = (value) => Number(value || 0).toLocaleString("en-US");

const figure = (value, format = withThousands) =>
    value === null || value === undefined ? "—" : format(value);

const VendorDashboard = ({
    stats,
    weeklyTickets,
    weeklyTotal,
    openCategories,
    openCategoriesTotal,
    recentTickets,
    activity,
    getVendorDashboard,
}) => {
    const contactName = localStorage.getItem("vendorContactName") || "Vendor";
    const firstName = contactName.trim().split(/\s+/)[0];

    useEffect(() => {
        getVendorDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cards = useMemo(() => {
        const topCategory = stats?.topOpenCategory;
        const oldest = stats?.oldestOpenTicket;
        return [
            {
                key: "open",
                label: "Total Open Tickets",
                value: figure(stats?.openTickets),
            },
            {
                key: "category",
                label: topCategory ? `${topCategory} Requests` : "Top Category Requests",
                value: figure(stats?.topOpenCategoryTickets),
                unit: topCategory ? "open" : null,
                note: topCategory ? null : "No open tickets",
            },
            {
                key: "oldest",
                label: "Oldest Open Ticket",
                value: oldest ? figure(oldest.ageDays) : "—",
                unit: oldest ? ageUnit(oldest.ageDays) : null,
                note: oldest ? oldest.no : "No open tickets",
            },
        ];
    }, [stats]);

    const categoryRows = useMemo(
        () =>
            (openCategories || []).map((row, index) => ({
                ...row,
                color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
            })),
        [openCategories]
    );

    const recent = useMemo(
        () => (recentTickets || []).map((ticket, index) => toView(ticket, index)),
        [recentTickets]
    );

    return (
        <div className="vp-page">
            <div className="vp-welcome">
                <div className="vp-wtxt">
                    <h1>Welcome back, {firstName} 👋</h1>
                    <p>Here's what's happening across your vendor account today.</p>
                </div>
                <Link className="vp-cta" to={TICKETS_PATH}>
                    <VendorTicketsCtaSvg />
                    Open Support Tickets
                    <span className="vp-pill">{stats?.openTickets ?? 0}</span>
                </Link>
            </div>

            <div className="vp-cards">
                {cards.map((card) => {
                    const { Icon, icClass } = CARD_ICONS[card.key];
                    return (
                        <div className="vp-card" key={card.key}>
                            <div className="vp-ctop">
                                <span className="vp-lbl">{card.label}</span>
                                <span className={`vp-ic ${icClass}`}>
                                    <Icon />
                                </span>
                            </div>
                            <div className="vp-num">
                                {card.value}
                                {card.unit ? (
                                    <span className="vp-num-unit">{card.unit}</span>
                                ) : null}
                            </div>
                            {card.note ? <div className="vp-delta flat">{card.note}</div> : null}
                        </div>
                    );
                })}
            </div>

            <div className="vp-grid2">
                <div className="vp-box">
                    <div className="vp-box-head">
                        <b>Recent Tickets</b>
                        <Link className="vp-link" to={TICKETS_PATH}>
                            View all Tickets
                            <VendorChevronRightSvg />
                        </Link>
                    </div>
                    <div className="vp-box-sub">Click any ticket to open it in the ticket inbox.</div>

                    {recent.length === 0 && (
                        <div className="vp-empty-row">No tickets have been raised yet.</div>
                    )}
                    {recent.map((ticket) => (
                        <Link
                            className="vp-tk"
                            to={ticketDetailPath(ticket)}
                            key={ticket.key}
                        >
                            <span className="vp-av" style={{ background: ticket.color }}>
                                {initials(ticket.person)}
                            </span>
                            <span className="vp-tb">
                                <span className="vp-r1">
                                    <span className="vp-who">{storeHeadline(ticket)}</span>
                                    <span className="vp-when">{ticket.time}</span>
                                </span>
                                <span className="vp-sub">{ticketLine(ticket)}</span>
                            </span>
                            <span className={`vp-cat c-${statusSuffix(ticket.status)}`}>
                                {ticket.status || "—"}
                            </span>
                        </Link>
                    ))}
                </div>

                <div className="vp-box">
                    <div className="vp-box-head">
                        <b>Recent Activity</b>
                        <span>Most recent first</span>
                    </div>
                    <div className="vp-box-sub">Latest updates on your account.</div>
                    {(activity || []).length === 0 && (
                        <div className="vp-empty-row">Nothing has happened on this account yet.</div>
                    )}
                    {(activity || []).map((item) => {
                        const badge = ACTIVITY_BADGES[item.category] || DEFAULT_BADGE;
                        const path = activityPath(item);
                        /* an entry that has somewhere to go is a link, the rest
                           stay plain rows - same markup either way */
                        const Row = path ? Link : "div";
                        return (
                            <Row
                                className="vp-tk"
                                key={item.id}
                                {...(path ? { to: path } : {})}
                            >
                                <span className="vp-av" style={{ background: badge.color }}>
                                    {badgeText(item, badge)}
                                </span>
                                <span className="vp-tb">
                                    <span className="vp-r1">
                                        <span className="vp-who">{item.title}</span>
                                        <span className="vp-when">
                                            {shortStamp(item.createdDate)}
                                        </span>
                                    </span>
                                    <span className="vp-sub">{item.sub}</span>
                                </span>
                            </Row>
                        );
                    })}
                </div>
            </div>

            <div className="vp-grid2">
                <div className="vp-box">
                    <div className="vp-box-head">
                        <b>Tickets raised — last 7 days</b>
                        <span>Total {weeklyTotal}</span>
                    </div>
                    {/* <div className="vp-box-sub">Daily volume of tickets opened by your members.</div> */}
                    <div className="vp-chart">
                        <TicketsBarChart data={weeklyTickets} />
                    </div>
                </div>

                <div className="vp-box">
                    <div className="vp-box-head">
                        <b>Open Tickets by Category</b>
                    </div>
                    <div className="vp-box-sub">
                        Where your open tickets sit across categories.
                    </div>
                    <div className="vp-donut-wrap">
                        <div className="vp-donut">
                            <TicketStatusDonut
                                data={categoryRows}
                                total={openCategoriesTotal}
                                totalLabel="Open Tickets"
                            />
                        </div>
                        <div className="vp-legend">
                            {categoryRows.map((row) => (
                                <div key={row.key}>
                                    <i style={{ background: row.color }} />
                                    {row.label} <b>{row.value}</b>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = ({ VendorDashboard }) => {
    return {
        stats: VendorDashboard?.stats || null,
        weeklyTickets: VendorDashboard?.weeklyTickets || [],
        weeklyTotal: VendorDashboard?.weeklyTotal || 0,
        openCategories: VendorDashboard?.openCategories || [],
        openCategoriesTotal: VendorDashboard?.openCategoriesTotal || 0,
        recentTickets: VendorDashboard?.recentTickets || [],
        activity: VendorDashboard?.activity || [],
        loading: VendorDashboard?.loading || false,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVendorDashboard: (data) => dispatch(getVendorDashboardRequest(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorDashboard);
